import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgGridModule } from 'ag-grid-angular';
import { BodyScrollEvent, ColDef, GridOptions, RowClickedEvent } from 'ag-grid-community';
import { ClaimsService } from '../../../core/services/claims.service';
import type {
  ICompanySearchDialogData,
  ICompanySearchDialogResult,
} from '../../interfaces/company-search-dialog.interface';
import { SEARCH_CONSTANTS } from '../../constants/company-search.constants';
import { OVERLAY_TEMPLATES } from '../../constants/brand-company.constants';
import { ICalculatedBrandCompany } from '../../interfaces/claims.model';
export type {
  ICompanySearchDialogData,
  ICompanySearchDialogResult,
} from '../../interfaces/company-search-dialog.interface';

@Component({
  selector: 'emma-company-search-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatTooltipModule, AgGridModule],
  templateUrl: './company-search-dialog.component.html',
  styleUrl: './company-search-dialog.component.scss',
  host: { class: 'e-company-search-dialog' },
})
export class CompanySearchDialogComponent implements OnInit {
  public readonly minSearchLength: number = SEARCH_CONSTANTS.MIN_SEARCH_LENGTH;
  private readonly debounceTimeout: number = SEARCH_CONSTANTS.DEBOUNCE_TIMEOUT_MS;
  private readonly maxResultsToDisplay: number = 25;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  public searchResults: Array<ICalculatedBrandCompany> = [];
  public allSearchResults: Array<ICalculatedBrandCompany> = [];
  public gridData: Array<ICalculatedBrandCompany> = [];
  public isLoading = false;
  public selectedCompany: ICalculatedBrandCompany | null = null;
  public searchText = '';
  public excessData = false;
  public count = 0;
  public showLoadAllMatches = false;
  public gridOptions!: GridOptions;
  public overlayNoRowsTemplate = OVERLAY_TEMPLATES.noRowsTemplateForCompany;
  public readonly agGridInputs = { tooltipShowDelayMilliSeconds: 0 };

  constructor(
    private readonly dialogRef: MatDialogRef<
      CompanySearchDialogComponent,
      ICompanySearchDialogResult | undefined
    >,
    private readonly claimsService: ClaimsService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ICompanySearchDialogData,
  ) {}

  public ngOnInit(): void {
    const columnDefs: Array<ColDef> = [
      {
        headerName: 'Company Name',
        field: 'brandCompanyName',
        flex: 1,
        sortable: true,
      },
    ];

    this.gridOptions = {
      rowData: this.gridData,
      columnDefs: columnDefs,
      rowHeight: 30,
      headerHeight: 0,
      suppressHorizontalScroll: false,
      onColumnResized: (event) => {
        if (event?.finished) {
          this.gridOptions.api?.refreshCells();
        }
      },
    } as GridOptions;
  }

  public get prePopulatedCompanyDisplay(): string {
    return this.data.prePopulatedCompany || ' ';
  }

  public onSearchInput(searchText: string): void {
    this.searchText = searchText?.trim() || '';
    this.selectedCompany = null;

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (this.searchText.length < this.minSearchLength) {
      this.searchResults = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.searchTimer = setTimeout(() => {
      this.performSearch(this.searchText);
    }, this.debounceTimeout);
  }

  private performSearch(companySearchTerm: string): void {
    if (Array.isArray(this.data.companies) && this.data.companies.length > 0) {
      const filtered: Array<ICalculatedBrandCompany> = this.data.companies.filter((c: ICalculatedBrandCompany) =>
        c.brandCompanyName.toLowerCase().includes(companySearchTerm.toLowerCase())
      );
      this.allSearchResults = filtered
        .sort((a: ICalculatedBrandCompany, b: ICalculatedBrandCompany) =>
          a.brandCompanyName.localeCompare(b.brandCompanyName, undefined, { sensitivity: 'base' })
        );
      this.processSearchResults();
      this.isLoading = false;
      return;
    }
    if (!this.data.projectCode) {
      this.isLoading = false;
      return;
    }
  }

  private processSearchResults(): void {
    if (this.allSearchResults.length > this.maxResultsToDisplay) {
      this.gridData = this.allSearchResults.slice(0, this.maxResultsToDisplay);
      this.count = this.allSearchResults.length;
      this.excessData = true;
    } else {
      this.gridData = this.allSearchResults;
      this.excessData = false;
    }
    this.searchResults = this.gridData;
    this.processGridSearchData(this.gridData);
    this.showLoadAllMatches = false;
  }

  private processGridSearchData(searchResults: Array<ICalculatedBrandCompany>): void {
    this.gridData = searchResults;
    if (this.gridOptions?.rowData) {
      this.gridOptions.rowData = this.gridData;
    }
    this.gridOptions?.api?.setRowData(this.gridData);
  }

  public loadAllSearchResults(): void {
    this.gridData = this.allSearchResults;
    this.searchResults = this.allSearchResults;
    this.excessData = false;
    this.processGridSearchData(this.gridData);
  }

  public onBodyScrollEnd(event: BodyScrollEvent): void {
    if (event.direction !== 'vertical') {
      return;
    }
    const bodyViewport = document.querySelector(
      '.e-company-search-grid div[ref="eBodyViewport"]',
    ) as HTMLElement;
    if (!bodyViewport || !this.excessData || this.showLoadAllMatches) {
      return;
    }
    this.showLoadAllMatches = this.isScrolledToBottom(bodyViewport);
  }

  private isScrolledToBottom(bodyViewport: HTMLElement): boolean {
    const scrollTop = bodyViewport.scrollTop;
    const scrollHeight = bodyViewport.scrollHeight;
    const clientHeight = bodyViewport.clientHeight;
    return scrollTop + clientHeight >= scrollHeight - 1;
  }

  public onSearchResultClicked(event: RowClickedEvent): void {
    const selectedCompanyRow: ICalculatedBrandCompany = event?.data;
    if (selectedCompanyRow) {
      this.onSelect(selectedCompanyRow);
    }
  }

  public onSelect(company: ICalculatedBrandCompany): void {
    this.selectedCompany = company;
  }

  public isSelected(company: ICalculatedBrandCompany): boolean {
    return this.selectedCompany?.id === company.id;
  }

  public clearSelection(): void {
    this.selectedCompany = null;
  }

  public onAdd(): void {
    if (!this.selectedCompany) {
      return;
    }

    this.dialogRef.close({
      company: this.selectedCompany,
    } as ICompanySearchDialogResult);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
